import { useQuery } from '@tanstack/react-query';
import { Navigate, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ErrorBoundarySuspense } from '@/components/error-boundary-suspense';
import { ProductionDeclarationStepper } from '@/features/production-declaration/components/shared/production-declaration-stepper';
import { hasVisitedProductionDeclarationGuideline } from '@/features/production-declaration/hooks/guideline/use-production-declaration-guideline-flow';
import { useProductionDeclarationLayout } from '@/features/production-declaration/hooks/use-production-declaration-layout';
import { ProductionDeclarationErrorFallback } from '@/features/production-declaration/production-declaration-error-fallback';
import { SupplierLayout } from '@/layouts/supplier-layout';
import { NotFoundPage } from '@/pages/errors/not-found-page';
import { getProductionDeclaration } from '@/services/apis/queries/production-declaration/get-production-declaration';

function ProductionDeclarationLayoutSkeleton() {
  return (
    <div className='space-y-6'>
      <div className='h-10 w-48 rounded-md bg-declaration-skeleton-warm' />
      <div className='rounded-2xl border border-declaration-border bg-white p-6'>
        <div className='flex items-center justify-between gap-4'>
          <div className='space-y-2'>
            <div className='h-8 w-40 rounded-md bg-declaration-skeleton-warm' />
            <div className='h-5 w-56 rounded-md bg-declaration-skeleton-warm' />
          </div>
          <div className='h-11 w-32 rounded-md bg-declaration-skeleton-warm' />
        </div>
      </div>
    </div>
  );
}

function ProductionDeclarationLoadingContent() {
  return null;
}

export function ProductionDeclarationLayout() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const {
    activeStepKey,
    isBaseRoute,
    loadErrorMessage,
    productionDeclarationId,
    queryKey,
    token,
  } = useProductionDeclarationLayout();
  const hasSupplierToken = Boolean(token);

  useEffect(() => {
    document.title = `${t('pageTitle')} - POM`;
  }, [t]);

  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => {
      if (wasDark) {
        root.classList.add('dark');
      }
    };
  }, []);

  const productionDeclarationQuery = useQuery({
    enabled:
      !isBaseRoute &&
      Boolean(activeStepKey) &&
      !(isAuthenticated && activeStepKey === 'guideline'),
    queryKey,
    queryFn: async () =>
      await getProductionDeclaration(productionDeclarationId, { token }),
  });

  if (isAuthLoading) {
    return (
      <SupplierLayout>
        <ProductionDeclarationLayoutSkeleton />
      </SupplierLayout>
    );
  }

  if (isBaseRoute) {
    const hasVisitedGuideline = hasVisitedProductionDeclarationGuideline(
      productionDeclarationId,
      token,
    );
    const supplierTarget =
      hasSupplierToken && hasVisitedGuideline
        ? '/production-declarations/$id/statement'
        : '/production-declarations/$id/guideline';

    return (
      <Navigate
        params={{ id: productionDeclarationId }}
        search={{ token }}
        to={
          isAuthenticated
            ? '/production-declarations/$id/statement'
            : supplierTarget
        }
      />
    );
  }

  if (!activeStepKey) {
    return (
      <div className='mx-auto flex h-screen w-full max-w-[1360px] items-center justify-center bg-transparent px-10 py-14'>
        <NotFoundPage />
      </div>
    );
  }

  if (isAuthenticated && activeStepKey === 'guideline') {
    return (
      <Navigate
        params={{ id: productionDeclarationId }}
        search={{ token }}
        to='/production-declarations/$id/statement'
      />
    );
  }

  if (productionDeclarationQuery.isPending) {
    return (
      <div className='mx-auto flex h-screen w-full max-w-[1360px] items-center justify-center bg-transparent px-10 py-14'>
        <ProductionDeclarationLoadingContent />
      </div>
    );
  }

  if (productionDeclarationQuery.isError) {
    return (
      <div className='mx-auto flex h-screen w-full max-w-[1360px] items-center justify-center bg-transparent px-10 py-14'>
        <ProductionDeclarationErrorFallback
          error={productionDeclarationQuery.error}
          loadErrorMessage={loadErrorMessage}
          queryKey={queryKey}
          resetErrorBoundary={() => {
            productionDeclarationQuery.refetch().catch(() => undefined);
          }}
        />
      </div>
    );
  }

  return (
    <SupplierLayout>
      {!isAuthenticated && activeStepKey ? (
        <div className='flex justify-end'>
          <ProductionDeclarationStepper
            canNavigateToGuideline={hasSupplierToken}
            currentStep={activeStepKey}
            productionDeclarationId={productionDeclarationId}
            token={token}
          />
        </div>
      ) : null}
      <ErrorBoundarySuspense
        errorFallbackWrapper={
          <div className='rounded-2xl border border-declaration-border bg-white p-8' />
        }
        loadingFallback={<ProductionDeclarationLoadingContent />}
        message={loadErrorMessage}
        queryKey={queryKey}
      >
        <Outlet />
      </ErrorBoundarySuspense>
    </SupplierLayout>
  );
}
